<script setup lang="ts">
import type { EditImportPreviewEntryInput, ImportPreviewEntry } from '../internal/types.ts'
import { StatusTag } from '@taler/ui'
import { computed, reactive, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import AmountInput from '@/app/components/AmountInput.vue'
import { AccountPicker } from '@/features/entries'
import { draftFromEntry, draftToInput } from '../internal/draft.ts'

const props = defineProps<{
  entry: ImportPreviewEntry
  disabled: boolean
}>()

const emit = defineEmits<{ save: [EditImportPreviewEntryInput]; viewDuplicate: [string] }>()

const { t, locale } = useI18n()
const currentLocale = computed(() => locale.value as 'en' | 'hu')

const draft = reactive(draftFromEntry(props.entry))

// The one field a reviewer's own action does not always set first: the duplicate default
// (`useImportJob`) applies `excluded: true` server-side on its own, so this is synced live
// rather than only at mount, the way every other field is - see `draftFromEntry`.
watch(
  () => props.entry.excluded,
  (excluded) => {
    draft.excluded = excluded
  },
)

function onCounterPicked(value: string | null): void {
  draft.counterAccountId = value ?? undefined
  commit()
}

function commit(): void {
  const input = draftToInput(draft)
  if (input !== undefined) emit('save', input)
}
</script>

<template>
  <tr class="border-b border-[color:var(--color-border)]">
    <td class="h-[var(--density-row-tall)] px-[var(--space-cell)] align-middle">
      <input
        v-model="draft.entryDateText"
        type="date"
        :disabled="disabled"
        :aria-label="t('imports.preview.date')"
        class="min-h-[var(--density-control)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] px-[var(--space-inline)] text-[length:var(--text-step-3)] text-[color:var(--color-text)] disabled:opacity-60"
        @change="commit"
      />
    </td>
    <td class="h-[var(--density-row-tall)] px-[var(--space-cell)] align-middle">
      <input
        v-model="draft.descriptionText"
        type="text"
        maxlength="2000"
        :disabled="disabled"
        :aria-label="t('imports.preview.description')"
        class="min-h-[var(--density-control)] w-full rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] px-[var(--space-inline)] text-[length:var(--text-step-3)] text-[color:var(--color-text)] disabled:opacity-60"
        @blur="commit"
      />
      <StatusTag v-if="entry.duplicateOfEntryId !== undefined" tone="neutral" class="mt-1">
        {{ t('imports.preview.duplicate') }}
        <button type="button" class="ml-1 underline" @click="emit('viewDuplicate', entry.duplicateOfEntryId)">
          {{ t('imports.preview.viewMatch') }}
        </button>
      </StatusTag>
    </td>
    <td class="h-[var(--density-row-tall)] px-[var(--space-cell)] align-middle">
      <AmountInput
        v-model="draft.amount"
        :currency="entry.amount.currency"
        :locale="currentLocale"
        :show-total="false"
        :disabled="disabled"
        :aria-label="t('imports.preview.amount')"
        class="w-32"
        @blur="commit"
      />
    </td>
    <td class="h-[var(--density-row-tall)] min-w-48 px-[var(--space-cell)] align-middle">
      <AccountPicker
        :id="`import-counter-${entry.id}`"
        :label="t('imports.preview.counterAccount')"
        :model-value="draft.counterAccountId"
        :disabled="disabled"
        include-system
        include-contacts
        @update:model-value="onCounterPicked"
      />
    </td>
    <td class="h-[var(--density-row-tall)] px-[var(--space-cell)] text-center align-middle">
      <input
        v-model="draft.excluded"
        type="checkbox"
        :disabled="disabled"
        :aria-label="t('imports.preview.excluded')"
        @change="commit"
      />
    </td>
  </tr>
</template>
